import { FC, ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ExpandMore, Category, Architecture } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import EventIcon from '@mui/icons-material/Event'
import { List, Paper, Typography, Divider, Box, ListItemButton, Chip, Grid, Stack, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'

import GRCircularProgress from '../../../../../components/GRCircularProgress/GRCircularProgress'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { endOfDayTimestamp } from '../../../../../helpers/endOfDayTimestamp'
import languageService from '../../../../../services/LanguageService'
import { useCurrentUserLanguage } from '../../../../account/hooks/useCurrentUserLanguage'
import { useCurrentMarket } from '../../../../markets'
import { useLiveEventBlueprintDiscontinued } from '../../../hooks/liveEventsBlueprints'
import { useLiveEventTagGroupsMapByEventTypeId } from '../../../hooks/useLiveEventTagGroups'
import { useTrackedEvents } from '../../../hooks/useTrackedEvents'
import { LiveEventTagGroup } from '../../../types/LiveEventTagGroup'
import { TrackedGame } from '../../../types/TrackedGame'
import { TrackingEvent } from '../../../types/TrackingEvents'
import { EventLevelTooltipContent } from '../../EventLevelTooltipContent'
import styles from './AllEventsTab.module.scss'

/**
 * Component representing event dialog All events tab
 */
type AllEventsTabProps = {
  currentTrackedGame?: TrackedGame
  currentEventTypeId?: string
  currentEventId?: string
  onEventSelect: (event: TrackingEvent) => void
  children?: ReactNode
}

export const AllEventsTab: FC<AllEventsTabProps> = ({ currentTrackedGame, onEventSelect, currentEventTypeId, currentEventId, children }) => {
  const { currentMarketIso } = useCurrentMarket()
  const { t } = useTranslation()
  const gameIds = useMemo(() => (currentTrackedGame ? [currentTrackedGame?.game.id] : []), [currentTrackedGame])
  const trackedEventsQuery = useTrackedEvents({ gameIds: gameIds, startTimestamp: 0, endTimestamp: endOfDayTimestamp(), marketIso: currentMarketIso })
  const liveEventTagGroupsMapByEventTypeId = useLiveEventTagGroupsMapByEventTypeId()
  const userLanguage = useCurrentUserLanguage()

  // Memoize the filtered events to avoid recalculating on every render
  const filteredEvents = useMemo(() => {
    const events = trackedEventsQuery.data?.events || {}
    const filtered: TrackingEvent[] = []

    Object.values(events).forEach((gameEvents) => {
      gameEvents.forEach((event) => {
        const tagGroup = liveEventTagGroupsMapByEventTypeId[event.typeId]
        if (tagGroup && tagGroup.id !== LiveEventTagGroup.DEFINING_OCCURRENCES) {
          filtered.push(event)
        }
      })
    })

    return filtered
  }, [trackedEventsQuery.data?.events, liveEventTagGroupsMapByEventTypeId])

  // Memoize blueprint name lookup to avoid repeated array searches
  const blueprintNamesMap = useMemo(() => {
    const map: { [blueprintId: string]: string } = {}
    if (currentTrackedGame?.blueprints) {
      currentTrackedGame.blueprints.forEach((blueprint) => {
        map[blueprint.blueprintId] = blueprint.names[userLanguage] || blueprint.names['en'] || ''
      })
    }
    return map
  }, [currentTrackedGame?.blueprints, userLanguage])

  // all events grouped by event type (ignoring blueprint categorization)
  const allEventsGroupedByType = useMemo(() => {
    const typeEvents: { [eventTypeId: string]: TrackingEvent[] } = {}
    const uniqueEventsMap: { [eventId: string]: TrackingEvent } = {}

    // First pass: collect all unique events (both with and without blueprint data)
    filteredEvents.forEach((event) => {
      uniqueEventsMap[event.eventId] = event
    })

    // Second pass: group by type
    Object.values(uniqueEventsMap).forEach((event) => {
      if (!typeEvents[event.typeId]) {
        typeEvents[event.typeId] = []
      }
      typeEvents[event.typeId].push(event)
    })

    return typeEvents
  }, [filteredEvents])

  const eventsGroupedByTypeAndBlueprint = useMemo(() => {
    const typeEvents: { [eventTypeId: string]: { blueprintEvents: { [blueprintId: string]: TrackingEvent[] }; individualEvents: TrackingEvent[] } } = {}

    Object.entries(allEventsGroupedByType).forEach(([eventTypeId, events]) => {
      typeEvents[eventTypeId] = {
        blueprintEvents: {},
        individualEvents: [],
      }
      events.forEach((event) => {
        if (event.blueprintEvolutionData) {
          // if the blueprintEvents object does not have the blueprintId, create it
          if (!typeEvents[eventTypeId].blueprintEvents[event.blueprintEvolutionData.blueprintId]) {
            typeEvents[eventTypeId].blueprintEvents[event.blueprintEvolutionData.blueprintId] = []
          }
          typeEvents[eventTypeId].blueprintEvents[event.blueprintEvolutionData.blueprintId].push(event)
        } else {
          typeEvents[eventTypeId].individualEvents.push(event)
        }
      })
    })

    // Create sorted version of typeEvents
    const sortedTypeEvents: { [eventTypeId: string]: { blueprintEvents: { [blueprintId: string]: TrackingEvent[] }; individualEvents: TrackingEvent[] } } = {}

    // Sort eventTypeIds by EventName (using language service translation)
    const sortedEventTypeEntries = Object.entries(typeEvents).sort(([eventTypeIdA], [eventTypeIdB]) => {
      const nameA = languageService.getTranslation('tags', eventTypeIdA)
      const nameB = languageService.getTranslation('tags', eventTypeIdB)
      return nameA.localeCompare(nameB)
    })

    // Process each event type
    sortedEventTypeEntries.forEach(([eventTypeId, events]) => {
      sortedTypeEvents[eventTypeId] = {
        blueprintEvents: {},
        individualEvents: [],
      }

      // Sort blueprintEvents by blueprintName
      const sortedBlueprintEntries = Object.entries(events.blueprintEvents).sort(([blueprintIdA], [blueprintIdB]) => {
        const nameA = blueprintNamesMap[blueprintIdA] || ''
        const nameB = blueprintNamesMap[blueprintIdB] || ''
        return nameA.localeCompare(nameB)
      })

      // Add sorted blueprint events
      sortedBlueprintEntries.forEach(([blueprintId, blueprintEvents]) => {
        // Sort events within each blueprint by event name
        const sortedEvents = [...blueprintEvents].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        sortedTypeEvents[eventTypeId].blueprintEvents[blueprintId] = sortedEvents
      })

      // Sort individualEvents by event name
      const sortedIndividualEvents = [...events.individualEvents].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      sortedTypeEvents[eventTypeId].individualEvents = sortedIndividualEvents
    })

    return sortedTypeEvents
  }, [allEventsGroupedByType, blueprintNamesMap])

  const getBlueprintName = useCallback(
    (blueprintId: string) => {
      return blueprintNamesMap[blueprintId] || ''
    },
    [blueprintNamesMap]
  )

  // The dialog opens on this tab, so it unfolds where the reader came from: the
  // current event's type and, inside it, the blueprint (or the "no blueprint"
  // group) holding the current event. Everything else starts collapsed.
  const currentEvent = useMemo(() => filteredEvents.find((event) => event.eventId === currentEventId), [filteredEvents, currentEventId])
  const currentBlueprintId = currentEvent?.blueprintEvolutionData?.blueprintId
  // The event's own type wins: it's in this tab's list before the accordions mount,
  // while the dialog's `currentEventTypeId` waits on the event query and can arrive
  // after `defaultExpanded` has already been read (e.g. when this game's events are cached).
  const expandedEventTypeId = currentEvent?.typeId ?? currentEventTypeId
  const isCurrentType = (eventTypeId: string) => eventTypeId === expandedEventTypeId

  const isLoading = trackedEventsQuery.isLoading || trackedEventsQuery.isFetching

  return isLoading ? (
    <GRCircularProgress />
  ) : (
    <Box sx={{ mb: 3 }}>
      {Object.entries(eventsGroupedByTypeAndBlueprint).map(([eventTypeId, events]) => {
        const blueprintEntries = Object.entries(events.blueprintEvents).filter(([, evts]) => evts && evts.length > 0)
        return (
          <Accordion key={eventTypeId} disableGutters defaultExpanded={isCurrentType(eventTypeId)}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={(theme) => ({
                backgroundColor: 'action.hover',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
                '&.Mui-expanded .MuiTypography-root': {
                  fontSize: theme.typography.h3.fontSize,
                  fontWeight: theme.typography.h3.fontWeight,
                },
              })}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <GRTooltip content={<EventLevelTooltipContent level="type" showLevelDetails />} styles={{ cursor: 'help', display: 'inline-flex' }}>
                  <Category fontSize="small" color="primary" aria-hidden />
                </GRTooltip>
                <Typography component="h3" variant="body1">
                  {languageService.getTranslation('tags', eventTypeId)}
                </Typography>
              </Stack>
            </AccordionSummary>
            {blueprintEntries.length > 0 && (
              <AccordionDetails>
                {blueprintEntries.map(([blueprintId, events]) => {
                  const blueprintName = getBlueprintName(blueprintId)
                  const hasName = blueprintName.length > 0
                  return (
                    <Accordion
                      key={blueprintId}
                      disableGutters
                      defaultExpanded={isCurrentType(eventTypeId) && !!currentEvent && blueprintId === currentBlueprintId}
                      TransitionProps={{ unmountOnExit: true }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={(theme) => ({
                          '&.Mui-expanded .MuiTypography-root': {
                            fontWeight: theme.typography.fontWeightBold,
                          },
                        })}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <GRTooltip content={<EventLevelTooltipContent level="blueprint" />} styles={{ cursor: 'help', display: 'inline-flex' }}>
                            <Architecture fontSize="small" color="primary" aria-hidden />
                          </GRTooltip>
                          <Typography component="h4" variant="body1" color={hasName ? 'inherit' : 'text.secondary'}>
                            {hasName ? blueprintName : t('live-events:unnamed_blueprint')}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails className={styles.accordionDetailsTint}>
                        <EventTypeSection
                          currentTrackedGame={currentTrackedGame}
                          events={events}
                          eventTypeId={eventTypeId}
                          currentEventId={currentEventId}
                          onItemClick={onEventSelect}
                          key={eventTypeId}
                          hideTitle={true}
                        />
                      </AccordionDetails>
                    </Accordion>
                  )
                })}
              </AccordionDetails>
            )}
            {events.individualEvents.length > 0 && (
              <AccordionDetails>
                <Accordion
                  key={`no-blueprint`}
                  disableGutters
                  defaultExpanded={isCurrentType(eventTypeId) && !!currentEvent && !currentBlueprintId}
                  TransitionProps={{ unmountOnExit: true }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={(theme) => ({
                      '&.Mui-expanded .MuiTypography-root': {
                        fontWeight: theme.typography.fontWeightBold,
                      },
                    })}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <GRTooltip content={<EventLevelTooltipContent level="blueprint" />} styles={{ cursor: 'help', display: 'inline-flex' }}>
                        <Architecture fontSize="small" color="primary" aria-hidden />
                      </GRTooltip>
                      <Typography component="h4" variant="body1">
                        {t('live-events:no_blueprint')}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails className={styles.accordionDetailsTint}>
                    <EventTypeSection
                      currentTrackedGame={currentTrackedGame}
                      events={events.individualEvents}
                      eventTypeId={eventTypeId}
                      currentEventId={currentEventId}
                      onItemClick={onEventSelect}
                      key={eventTypeId}
                      hideTitle={true}
                    />
                  </AccordionDetails>
                </Accordion>
              </AccordionDetails>
            )}
          </Accordion>
        )
      })}
    </Box>
  )
}

type EventTypeSectionProps = {
  events: TrackingEvent[]
  currentTrackedGame?: TrackedGame
  eventTypeId?: string
  currentEventId?: string
  marginBottom?: number
  onItemClick: (event: TrackingEvent) => void
  hideTitle?: boolean
}

const EventTypeSection: FC<EventTypeSectionProps> = ({
  events,
  currentTrackedGame,
  eventTypeId,
  currentEventId,
  onItemClick,
  marginBottom = 3,
  hideTitle = false,
}) => {
  const { t } = useTranslation()
  const isDiscontinued = useLiveEventBlueprintDiscontinued({
    blueprintId: events.length > 0 ? events[0].blueprintEvolutionData?.blueprintId : undefined,
    trackedGame: currentTrackedGame,
  })

  // Memoize the event type name to avoid repeated translation calls
  const eventTypeName = useMemo(() => {
    return eventTypeId ? languageService.getTranslation('tags', eventTypeId) : ''
  }, [eventTypeId])

  // Memoize the event click handler to prevent unnecessary re-renders
  const handleEventClick = useCallback(
    (event: TrackingEvent) => {
      onItemClick(event)
    },
    [onItemClick]
  )

  return (
    <>
      {eventTypeId && !hideTitle && (
        <Typography variant="h3" sx={{ mb: 2 }}>
          {eventTypeName}
        </Typography>
      )}
      <List component={Paper} disablePadding sx={{ mb: marginBottom }}>
        {events.map((event, index, list) => {
          return (
            <Box key={event.eventId}>
              <ListItemButton onClick={() => handleEventClick(event)}>
                <Grid container alignItems="center" justifyContent="space-between" wrap="wrap" rowSpacing={1}>
                  <Grid item xs={12} md>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <GRTooltip content={t('live-events:event_tooltip')} styles={{ cursor: 'help', display: 'inline-flex' }}>
                        <EventIcon fontSize="small" color="primary" aria-hidden />
                      </GRTooltip>
                      <Typography variant="body1">{event.name}</Typography>
                    </Stack>
                  </Grid>
                  {(currentEventId === event.eventId || event.active === false || isDiscontinued) && (
                    <Grid item xs={12} md="auto">
                      <Grid container alignItems="center" justifyContent="flex-end" gap={2} wrap="wrap">
                        {currentEventId === event.eventId && <CheckIcon color="success" />}
                        {event.active === false && <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ m: 0 }} />}
                        {isDiscontinued && <Chip label={t('live-events:live_event_evolution_type_discontinued')} color="error" size="small" sx={{ m: 0 }} />}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </ListItemButton>

              {index < list.length - 1 && <Divider />}
            </Box>
          )
        })}
      </List>
    </>
  )
}
